import { useEffect, useState, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Learn from './pages/Learn';
import Adopt from './pages/Adopt';
import TheHerd from './pages/TheHerd';
import Ranchette from './pages/Ranchette';
import Ranch from './pages/Ranch';
import FacilityBuilder from './pages/FacilityBuilder';
import Community from './pages/Community';
import AdminPanel from './pages/AdminPanel';
import Trainer from './pages/Trainer';
import TrainerSuccess from './pages/TrainerSuccess';
import VirtualCorral from './pages/VirtualCorral';
import VirtualCorralSuccess from './pages/VirtualCorralSuccess';
import HerdSuccess from './pages/HerdSuccess';
import AuthDebug from './pages/AuthDebug';
import Mercantile from './pages/Mercantile';
import SplashScreen from './components/SplashScreen';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { Toaster, toast } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 10000 // 10 second timeout
});

// Token helpers
const saveToken = (token) => {
  try { localStorage.setItem('session_token', token); } catch(e) {}
  try { sessionStorage.setItem('session_token', token); } catch(e) {}
};

const getToken = () => {
  try { 
    const token = localStorage.getItem('session_token');
    if (token) return token;
  } catch(e) {}
  try { 
    const token = sessionStorage.getItem('session_token');
    if (token) return token;
  } catch(e) {}
  return null;
};

const clearToken = () => {
  try { localStorage.removeItem('session_token'); } catch(e) {}
  try { sessionStorage.removeItem('session_token'); } catch(e) {}
};

// Add token to all requests
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [showSplash, setShowSplash] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Process OAuth callback
  const processAuthCallback = useCallback(async (sessionId) => {
    try {
      const res = await fetch(`${API}/auth/process-session`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'X-Session-ID': sessionId 
        },
        credentials: 'include'
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Auth failed: ${res.status} - ${errorText}`);
      }
      
      const data = await res.json();
      if (data.session_token) {
        saveToken(data.session_token);
      }
      setUser(data.user);
      setProgress(data.progress);
      
      // Clear hash from URL
      window.history.replaceState(null, '', window.location.pathname);
      return true;
    } catch (e) {
      console.error('Auth callback error:', e);
      setAuthError('Login failed. Please try again.');
      toast.error('Login failed. Please try again.');
      // Clear the hash so we don't keep trying
      window.history.replaceState(null, '', window.location.pathname);
      return false;
    }
  }, []);

  // Check existing session
  const checkSession = useCallback(async () => {
    try {
      const res = await api.get('/auth/session');
      setUser(res.data.user);
      setProgress(res.data.progress);
      return true;
    } catch (e) {
      // Only clear token if we get 401/403, not on network errors
      if (e.response && (e.response.status === 401 || e.response.status === 403)) {
        clearToken();
      }
      return false;
    }
  }, []);

  useEffect(() => {
    // Prevent running twice
    if (window._authStarted) return;
    window._authStarted = true;

    const init = async () => {
      try {
        const hash = window.location.hash;
        
        // Handle OAuth callback first
        if (hash && hash.includes('session_id=')) {
          const params = new URLSearchParams(hash.substring(1));
          const sessionId = params.get('session_id');
          
          if (sessionId) {
            await processAuthCallback(sessionId);
          }
          setLoading(false);
          return;
        }

        // Check existing session
        const hasToken = getToken();
        if (hasToken) {
          await checkSession();
        }
      } catch (e) {
        console.error('Init error:', e);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [processAuthCallback, checkSession]);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {}
    clearToken();
    setUser(null);
    setProgress(null);
    window._authStarted = false;
  };

  const refreshProgress = async () => {
    try {
      const res = await api.get('/auth/session');
      setProgress(res.data.progress);
      setUser(res.data.user);
    } catch (e) {}
  };

  // Splash screen
  if (showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#F5EDE3'}}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{borderColor: '#D66238'}}></div>
          <p style={{color: '#372F2F'}}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors />
      <PWAInstallPrompt />
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
        <Route path="/dashboard" element={user ? <Dashboard user={user} progress={progress} onLogout={handleLogout} refreshProgress={refreshProgress} /> : <Navigate to="/" />} />
        <Route path="/learn" element={user ? <Learn user={user} progress={progress} onLogout={handleLogout} refreshProgress={refreshProgress} /> : <Navigate to="/" />} />
        <Route path="/adopt" element={user ? <Adopt user={user} progress={progress} onLogout={handleLogout} refreshProgress={refreshProgress} /> : <Navigate to="/" />} />
        <Route path="/the-herd" element={user ? <TheHerd user={user} progress={progress} onLogout={handleLogout} refreshProgress={refreshProgress} /> : <Navigate to="/" />} />
        <Route path="/ranchette" element={user ? <Ranchette user={user} progress={progress} onLogout={handleLogout} refreshProgress={refreshProgress} /> : <Navigate to="/" />} />
        <Route path="/ranch" element={user ? <Ranch user={user} progress={progress} onLogout={handleLogout} refreshProgress={refreshProgress} /> : <Navigate to="/" />} />
        <Route path="/facility-builder" element={user ? <FacilityBuilder user={user} progress={progress} onLogout={handleLogout} refreshProgress={refreshProgress} /> : <Navigate to="/" />} />
        <Route path="/community" element={user ? <Community user={user} progress={progress} onLogout={handleLogout} refreshProgress={refreshProgress} /> : <Navigate to="/" />} />
        <Route path="/admin" element={user ? <AdminPanel user={user} onLogout={handleLogout} refreshProgress={refreshProgress} /> : <Navigate to="/" />} />
        <Route path="/trainer" element={user ? <Trainer user={user} progress={progress} onLogout={handleLogout} refreshProgress={refreshProgress} /> : <Navigate to="/" />} />
        <Route path="/trainer-success" element={<TrainerSuccess />} />
        <Route path="/virtual-corral" element={user ? <VirtualCorral user={user} progress={progress} onLogout={handleLogout} refreshProgress={refreshProgress} /> : <Navigate to="/" />} />
        <Route path="/virtual-corral-success" element={<VirtualCorralSuccess />} />
        <Route path="/herd-success" element={<HerdSuccess />} />
        <Route path="/auth-debug" element={<AuthDebug />} />
        <Route path="/mercantile" element={user ? <Mercantile user={user} progress={progress} onLogout={handleLogout} refreshProgress={refreshProgress} /> : <Navigate to="/" />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
